import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Colors } from 'mm-theme-configuration';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { Link } from '../../partials/link/Link';
import { ImageProps, Image } from '../../partials/image/Image';
import { createImageHeightCalculationMethods } from '../../partials/image/image.utils';
import { P, semanticCSS } from '../../../typography/semanticTags';
import { useTheme } from '../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

interface ResizingCarouselImageProps extends ImageProps {
  imageLink: string;
  title: string;
  links?: Array<{text: string; link: string}>;
}

interface ResizingCarouselProps {
  images: ResizingCarouselImageProps[];
}

const getStyles = (colors: Colors) => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      width: '100%',
      height: '750px',
      position: 'relative',
      gap: '25px',
      [MEDIA_BREAKPOINTS.medium]: {
        height: '700px',
        gap: '30px',
        overflowX: 'scroll',
        padding: '0px 30px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: '450px',
        gap: '10px',
        padding: '0px 10px',
      },
    },
    textWrapper: {
      position: 'absolute',
      bottom: '26px',
      left: '26px',
      zIndex: 2,
    },
    title: {
      color: colors.white,
    },
    link: {
      textDecoration: 'underline',
      color: colors.white,
      ':hover': {
        color: colors.secondary,
      },
    },
    linkWrapper: {
      display: 'flex',
      flexDirection: 'row',
    },
    linksWrapper: {
      display: 'flex',
      flexDirection: 'row',
    },
    pipe: {
      color: colors.white,
      margin: '0px 15px',
    },

  });
};

const getImageWrapperStyle = (isHover: boolean) => {
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      height: '100%',
      width: isHover ? '675px' : '230px',
      transition: 'width 0.4s 0.1s ease-out',
      ...(isHover ? { flexShrink: 0 } : {}),
      [MEDIA_BREAKPOINTS.medium]: {
        width: '600px',
        flexShrink: 0,
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '310px',
        flexShrink: 0,
      },
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: isHover ? '675px' : '0px',
      height: '100%',
      zIndex: 1,
      background: 'linear-gradient(0deg, #212121 0%, rgba(33, 33, 33, 0.00) 35.5%, rgba(33, 33, 33, 0.00) 100%)',
      transition: isHover ? 'opacity 0.15s 0.35s ease-in' : '',
      opacity: isHover ? 1 : 0,
      [MEDIA_BREAKPOINTS.medium]: {
        width: '600px',
        opacity: 1,
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '310px',
      },
    },
    overlay2: {
      position: 'absolute',
      top: 0,
      left: 0,
      opacity: isHover ? 0 : 1,
      background: 'rgb(241, 230, 221, 0.5)',
      height: '750px',
      width: '230px',
      transition: !isHover ? 'opacity 0.25s 0.2s linear' : '',
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none',
      },
    },
  });
};

export const ResizingCarousel: React.FunctionComponent<ResizingCarouselProps> = props => {
  const { images } = props;
  const { colors }: { colors: Colors } = useTheme();
  const imageHeightCalculationMethods = createImageHeightCalculationMethods('100%_HEIGHT', '100%_HEIGHT', '100%_HEIGHT');
  const [hoverIndex, setHoverIndex] = useState(1);
  const styles = getStyles(colors);

  const largeFontSizes = {
    fontSizeSmall: TYPOGRAPHY_SIZE.LARGE,
    fontSizeMedium: TYPOGRAPHY_SIZE.LARGE,
    fontSizeLarge: TYPOGRAPHY_SIZE.LARGE,
  };

  const normalFontSizes = {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL,
  };

  return (
    <ErrorBoundary>
      <div className={css(styles.wrapper)}>
        {images.map((image, index) => {
          const { imageLink, title, links, ...imageProps } = image;
          const isHover = hoverIndex === index;
          const imageStyles = getImageWrapperStyle(isHover);
          return (
            <div className={css(imageStyles.wrapper)} onMouseEnter={() => setHoverIndex(index)}>
              <Link href={imageLink}>
                <Image
                  {...imageProps}
                  imageHeightCalculationMethods={imageHeightCalculationMethods}
                  imageAspectRatios={{ large: { x: 9, y: 10 }, medium: { x: 9, y: 10 }, small: { x: 9, y: 10 } }}
                />
                <div className={css(imageStyles.overlay2)} />
                <div className={css(imageStyles.overlay)}>
                  <div className={css(styles.textWrapper)}>
                    <P styles={semanticCSS(styles.title)} {...largeFontSizes}>{title}</P>
                    <div className={css(styles.linksWrapper)}>
                      {links?.map((linkData, linkIndex) => {
                        return (
                          <div className={css(styles.linkWrapper)}>
                            <Link href={linkData.link}>
                              <P styles={semanticCSS(styles.link)} {...normalFontSizes}>{linkData.text}</P>
                            </Link>
                            {linkIndex === 0 && links.length === 2 && (
                            <P {...normalFontSizes} styles={semanticCSS(styles.pipe)}>|</P>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
      </div>
    </ErrorBoundary>
  );
};
